import { useEffect, useRef, useState } from 'react';
import { useEditor } from '../../state/store';
import {
  fetchPickerProducts,
  fetchPickerCollections,
  type PickerProduct,
  type PickerCollection,
} from '../../api/client';

/**
 * Modal genérico de picker — usado por ProductField e CollectionField.
 *
 * Resolve o problema de "só aparecem 20 produtos no <select>":
 * o metadata inicial do editor só traz os 20 primeiros do paycentral.
 * Loja com 500 produtos = lojista escolhendo entre 20.
 *
 * Aqui buscamos sob demanda via endpoint paginado da engine
 * (`/editor/.../products` ou `/editor/.../collections-list`), com:
 *   - Search com debounce 300ms (produtos só — paycentral não suporta
 *     `?q=` em coleções, e a lista de coleções é pequena então não é
 *     um problema real)
 *   - Paginação clássica (◀ Página X de Y ▶) — escolha do usuário
 *   - Click no card seleciona e fecha o modal
 *   - Highlight do item atualmente selecionado
 *   - ESC fecha
 *
 * Acessibilidade: role="dialog", aria-modal, focus trap simples.
 */

interface BasePickerProps {
  /** Valor atualmente selecionado (uuid). */
  value: string;
  onChange: (uuid: string) => void;
  onClose: () => void;
}

// ----------------------------------------------------------------------------
// Shared modal shell
// ----------------------------------------------------------------------------

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white text-text shadow-2xl">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-sm font-semibold">{title}</h2>
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="flex h-7 w-7 items-center justify-center rounded-md text-text-subdued hover:bg-surface hover:text-text"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  onChange,
  busy,
}: {
  page: number;
  totalPages: number;
  onChange: (next: number) => void;
  busy: boolean;
}) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between border-t border-border bg-surface px-4 py-2.5 text-xs text-text-subdued">
      <span>
        Página <strong className="text-text">{page}</strong> de{' '}
        <strong className="text-text">{totalPages}</strong>
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onChange(page - 1)}
          disabled={page <= 1 || busy}
          className="flex h-7 w-7 items-center justify-center rounded-md text-text-subdued hover:bg-white hover:text-text disabled:opacity-40 disabled:hover:bg-transparent"
          aria-label="Página anterior"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>
        <button
          onClick={() => onChange(page + 1)}
          disabled={page >= totalPages || busy}
          className="flex h-7 w-7 items-center justify-center rounded-md text-text-subdued hover:bg-white hover:text-text disabled:opacity-40 disabled:hover:bg-transparent"
          aria-label="Próxima página"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="flex animate-pulse items-center gap-3 px-4 py-3">
      <div className="h-12 w-12 rounded bg-surface" />
      <div className="flex-1 space-y-2">
        <div className="h-3 w-1/2 rounded bg-surface" />
        <div className="h-2 w-1/4 rounded bg-surface" />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Product picker
// ----------------------------------------------------------------------------

function formatBRL(amount: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount);
}

export function ProductPickerModal({ value, onChange, onClose }: BasePickerProps) {
  const session = useEditor((s) => s.session);
  const [items, setItems] = useState<PickerProduct[]>([]);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
    Debounce na busca: re-fetch após 300ms sem digitar pra não atropelar
    o paycentral. Search também reseta pra página 1 (resultado novo,
    paginação antiga não faz sentido).
  */
  const debounceRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!session) return;
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      void load(q, 1);
    }, 300);
    return () => window.clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  // Carrega ao mudar de página (sem debounce — usuário cliciou no botão).
  useEffect(() => {
    if (!session) return;
    void load(q, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  async function load(query: string, pageNum: number) {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetchPickerProducts(session, { q: query, page: pageNum });
      setItems(r.products || []);
      setTotalPages(r.totalPages || 1);
      setTotal(r.total || 0);
      setPage(r.page || pageNum);
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar');
      setItems([]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <ModalShell title="Selecionar produto" onClose={onClose}>
      <div className="border-b border-border px-4 py-3">
        <div className="relative">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-icon-subdued"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
            placeholder="Buscar por nome do produto..."
            className="w-full rounded-md border border-border bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-primary"
          />
        </div>
        {total > 0 && !busy && (
          <p className="mt-2 text-xs text-text-subdued">
            {total} produto{total === 1 ? '' : 's'} encontrado{total === 1 ? '' : 's'}
            {q && ' para esta busca'}
          </p>
        )}
      </div>

      <div className="flex-1 overflow-y-auto">
        {busy && items.length === 0 && (
          <>
            <Skeleton />
            <Skeleton />
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        )}
        {error && (
          <div className="p-6 text-center text-sm text-red-600">{error}</div>
        )}
        {!busy && !error && items.length === 0 && (
          <div className="p-6 text-center text-sm text-text-subdued">
            {q ? 'Nenhum produto encontrado pra essa busca.' : 'Nenhum produto cadastrado.'}
          </div>
        )}
        {items.map((p) => {
          const isSelected = String(value) === p.uuid;
          return (
            <button
              key={p.uuid}
              onClick={() => {
                onChange(p.uuid);
                onClose();
              }}
              className={
                'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface ' +
                (isSelected ? 'bg-primary/5' : '')
              }
            >
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded border border-border bg-surface">
                {p.image ? (
                  <img
                    src={p.image}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-icon-subdued">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <circle cx="9" cy="9" r="2" />
                      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-text">{p.name}</div>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-text-subdued">
                  <span>{formatBRL(p.amount)}</span>
                  {p.sku && (
                    <>
                      <span className="text-icon-subdued">·</span>
                      <span className="truncate">SKU: {p.sku}</span>
                    </>
                  )}
                  {!p.active && (
                    <>
                      <span className="text-icon-subdued">·</span>
                      <span className="rounded bg-surface px-1.5 py-0.5 text-[10px] font-medium text-text-subdued">
                        inativo
                      </span>
                    </>
                  )}
                </div>
              </div>
              {isSelected && (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="shrink-0 text-primary">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              )}
            </button>
          );
        })}
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={setPage} busy={busy} />
    </ModalShell>
  );
}

// ----------------------------------------------------------------------------
// Collection picker — sem search no backend (paycentral não suporta `?q=`
// em /collections); fazemos client-side filter sobre a página atual.
// ----------------------------------------------------------------------------

export function CollectionPickerModal({ value, onChange, onClose }: BasePickerProps) {
  const session = useEditor((s) => s.session);
  const [items, setItems] = useState<PickerCollection[]>([]);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    void load(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  async function load(pageNum: number) {
    if (!session) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetchPickerCollections(session, { page: pageNum });
      setItems(r.collections || []);
      setTotalPages(r.totalPages || 1);
      setTotal(r.total || 0);
      setPage(r.page || pageNum);
    } catch (e: any) {
      setError(e?.message || 'Erro ao carregar');
      setItems([]);
    } finally {
      setBusy(false);
    }
  }

  // Filter client-side (paycentral não filtra coleções por q)
  const filtered = q
    ? items.filter((c) => c.title.toLowerCase().includes(q.toLowerCase()))
    : items;

  return (
    <ModalShell title="Selecionar coleção" onClose={onClose}>
      <div className="border-b border-border px-4 py-3">
        <div className="relative">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-icon-subdued"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            autoFocus
            placeholder="Filtrar coleções desta página..."
            className="w-full rounded-md border border-border bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-primary"
          />
        </div>
        <p className="mt-2 text-xs text-text-subdued">
          {total > 0 && !busy
            ? `${total} coleção${total === 1 ? '' : 'ões'} no total · página com ${filtered.length} ${q ? 'após filtro' : 'itens'}`
            : busy
              ? 'Carregando...'
              : 'Nenhuma coleção encontrada.'}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {busy && items.length === 0 && (
          <>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        )}
        {error && (
          <div className="p-6 text-center text-sm text-red-600">{error}</div>
        )}
        {!busy && !error && filtered.length === 0 && items.length > 0 && (
          <div className="p-6 text-center text-sm text-text-subdued">
            Nenhuma coleção bate com "{q}" nesta página.
          </div>
        )}
        {filtered.map((c) => {
          const isSelected = String(value) === c.uuid;
          return (
            <button
              key={c.uuid}
              onClick={() => {
                onChange(c.uuid);
                onClose();
              }}
              className={
                'flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface ' +
                (isSelected ? 'bg-primary/5' : '')
              }
            >
              <div className="h-12 w-12 shrink-0 overflow-hidden rounded border border-border bg-surface">
                {c.image ? (
                  <img
                    src={c.image}
                    alt=""
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-icon-subdued">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
                      <rect x="3" y="3" width="7" height="7" rx="1" />
                      <rect x="14" y="3" width="7" height="7" rx="1" />
                      <rect x="3" y="14" width="7" height="7" rx="1" />
                      <rect x="14" y="14" width="7" height="7" rx="1" />
                    </svg>
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-text">{c.title}</div>
                {c.handle && (
                  <div className="mt-0.5 truncate text-xs text-text-subdued">
                    /{c.handle}
                  </div>
                )}
              </div>
              {isSelected && (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="shrink-0 text-primary">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
              )}
            </button>
          );
        })}
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={setPage} busy={busy} />
    </ModalShell>
  );
}
